import { useMemo, useState } from 'react'
import { FolderOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AccessDenied } from '@/components/common/AccessDenied'
import { PageShell } from '@/components/common/PageShell'
import { SegmentedControl } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { useMe } from '@/features/settings/hooks/useMe'
import { useCompanies, useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { CompanyDrive } from './drive/CompanyDrive'
import { UnplacedFilesNotice } from './drive/UnplacedFilesNotice'
import { StaffDocumentsTab } from './StaffDocumentsTab'

type DocumentsTabId = 'company' | 'staff'

const TAB_OPTIONS: { value: DocumentsTabId; label: string }[] = [
  { value: 'company', label: 'Company Drive' },
  { value: 'staff', label: 'Staff documents' },
]

export function DocumentsPage(): React.ReactElement {
  const { can } = usePermissions()
  const { data: me } = useMe()

  const canReadAllCompanies = can('companies', 'read')
  const canCreate = can('documents', 'create')
  const canUpdate = can('documents', 'update')
  const canDelete = can('documents', 'delete')
  // Same rules as canReadCompanyLibrary / canManageCompanyLibrary on the backend.
  const canReadCompany = can('documents', 'read_company') || can('documents', 'read')
  const canManageCompany = can('documents', 'manage_company') || canUpdate || canDelete
  const canReadStaffOthers = can('documents', 'read_staff_others')
  const canManageStaffOthers = can('documents', 'manage_staff_others')
  const canReadOwn = can('documents', 'read')
  const allCompaniesQuery = useCompanies(undefined, { enabled: canReadAllCompanies })
  const myCompaniesQuery = useMyCompanies({ enabled: !canReadAllCompanies })
  const companies = canReadAllCompanies
    ? (allCompaniesQuery.data ?? [])
    : (myCompaniesQuery.data ?? [])

  const [companyId, setCompanyId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('companyId')
  })
  const effectiveCompanyId = companyId ?? companies[0]?._id ?? null
  const selectedCompanyIdSet = useMemo(
    () => (effectiveCompanyId ? new Set([effectiveCompanyId]) : new Set<string>()),
    [effectiveCompanyId]
  )

  const meId = me?._id ?? ''
  const canAnyCompanyAction = canReadCompany || canManageCompany
  const canAnyStaffAction =
    canReadOwn ||
    canReadStaffOthers ||
    canCreate ||
    canUpdate ||
    canDelete ||
    canManageStaffOthers
  const showCompanyTab = canAnyCompanyAction
  const showStaffTab = canAnyStaffAction

  const fallbackTab: DocumentsTabId = showCompanyTab ? 'company' : 'staff'
  const [activeTab, setActiveTab] = useState<DocumentsTabId>(fallbackTab)
  const activeTabAllowed =
    (activeTab === 'company' && showCompanyTab) ||
    (activeTab === 'staff' && showStaffTab)
  const visibleActiveTab = activeTabAllowed ? activeTab : fallbackTab

  const handleToggleCompany = (id: string | null): void => {
    if (id === null) return
    setCompanyId(id)
  }

  const breadcrumbItems = [
    { label: 'Workforce', to: '/dashboard' },
    { label: 'Documents' },
  ]

  if (!showCompanyTab && !showStaffTab) {
    return (
      <PageShell
        breadcrumbItems={breadcrumbItems}
        icon={FolderOpen}
        title="Documents"
        className="gap-0"
        hideHeader
      >
        <AccessDenied resourceName="Documents" />
      </PageShell>
    )
  }

  const staffTab = meId ? (
    <StaffDocumentsTab
      meId={meId}
      companyId={effectiveCompanyId}
      canReadOwn={canReadOwn}
      canReadOthers={canReadStaffOthers}
      canCreate={canCreate}
      canUpdate={canUpdate}
      canDelete={canDelete}
      canManageOthers={canManageStaffOthers}
    />
  ) : (
    <p className="py-6 text-sm text-gray-500 dark:text-gray-400">Loading profile…</p>
  )
  const showStaff = visibleActiveTab === 'staff'
  const canManageCandidates = can('users', 'read') && (can('users', 'create') || can('users', 'update'))

  return (
    <PageShell
      breadcrumbItems={breadcrumbItems}
      icon={FolderOpen}
      title="Documents"
      description="Company files in folders, and staff documents."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-5">
        <WorkspaceToolbar
          filter={
            showCompanyTab && showStaffTab ? (
              <SegmentedControl
                ariaLabel="Documents view"
                size="sm"
                options={TAB_OPTIONS}
                value={visibleActiveTab}
                onChange={setActiveTab}
              />
            ) : undefined
          }
          actions={
            canManageCandidates ? (
              <Link
                to="/candidates"
                className="inline-flex h-8 min-h-8 items-center justify-center whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                Candidate interviews
              </Link>
            ) : undefined
          }
        >
          {showStaff && companies.length > 0 ? (
            <CompanyChips
              companies={companies.map((c) => ({
                _id: c._id,
                name: c.name,
                color: (c as unknown as { color?: string }).color,
              }))}
              selectedCompanyIds={selectedCompanyIdSet}
              onToggle={handleToggleCompany}
              hideAll
            />
          ) : null}
        </WorkspaceToolbar>

        {showStaff ? (
          <>
            {canReadStaffOthers && canManageStaffOthers ? (
              <UnplacedFilesNotice companies={companies} />
            ) : null}
            {staffTab}
          </>
        ) : (
          <CompanyDrive companies={companies} canManage={canManageCompany} canDelete={canDelete} />
        )}
      </div>
    </PageShell>
  )
}

DocumentsPage.displayName = 'DocumentsPage'
